import React, { useState, useEffect, useMemo, createRef } from 'react';
import Api from '../../api/global.service';
import { Link, useHistory } from 'react-router-dom';
import { Panel } from '../../components/panel/panel.jsx';
import CardBasic from '../../components/card/card-basic.jsx';
import CardContainer from '../../components/card/card-container.jsx';
import BlockUi from 'react-block-ui';
import GoogleMapReact from 'google-map-react';
import env from './../../environment.js';
import FormSelectMaterial from '../../nuup/componentes/Forms/FormSelectMaterial.jsx';
import useArrowTextureOverlay from '../../hooks/useArrowTextureOverlay.jsx';
import NorthIcon from '../../components/map/north-icon.jsx';
import useCroquisHook from '../../hooks/useCroquisHook.jsx';
import MapText from '../../components/card/map-text.jsx';
import useGetColindancias from '../../hooks/useGetColindancias.jsx';
import MarkerInfoWindow from '../../components/map/marker-infowindow.jsx';
import parsePoint from '../../utils/parsePoint.js';
import ParcelasAlertas from './parcelas-alertas.js';
import Swal from 'sweetalert2';
import CardTitleParcela from '../../components/card/card-title-parcela.jsx';
import deleteCroquis from '../../nuup/utils/deleteCroquis.js';
import abrirDibujoCroquis from '../../nuup/utils/openImageCroquis.js';
import ModalKMLDownload from '../../components/modal/modal-kml-download.jsx';
import useGeoPuntosLayer from '../../hooks/useGeoPuntosLayer.jsx';
import useEtiquetas from '../../hooks/useEtiquetas';
import transformWord from '../../utils/transformWord';
import getApiError from '../../utils/getApiError';
import validatePolygonFile, { POLYGON_FILE_ACCEPT, MAX_POLYGON_FILE_SIZE_MB } from '../../utils/validatePolygonFile';
import { useCoopConfig, usaMiel } from '../../components/content/coop-config.jsx';
import SimbologiaLegend from '../../components/map/simbologia-legend.jsx';

// Inline colindancia box floating over the map
const ColindanciaBox = ({ title, data, style }) => {
  if (!data?.colindante && !data?.propietario) return null;
  return (
    <div
      style={{
        position: 'absolute',
        zIndex: 10,
        backgroundColor: 'rgba(255,255,255,0.92)',
        border: '1px solid #c8d1a0',
        borderRadius: 6,
        padding: '5px 10px',
        maxWidth: 160,
        textAlign: 'center',
        boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
        pointerEvents: 'none',
        ...style,
      }}>
      <div style={{ fontWeight: 700, fontSize: 11, color: '#4A6700', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>
      {data?.colindante && <div style={{ fontSize: 11, color: '#333', marginTop: 1 }}>{data.colindante}</div>}
      {data?.colindante_otro && <div style={{ fontSize: 11, color: '#555' }}>{data.colindante_otro}</div>}
      {data?.propietario && <div style={{ fontSize: 10, color: '#666', marginTop: 1 }}>{data.propietario}</div>}
    </div>
  );
};

const ParcelaProfile = ({ uuid, parcela, parcelaData, centroid, blocking, width }) => {
  const coopConfig = useCoopConfig();
  const { etiquetas } = useEtiquetas();
  const history = useHistory();
  const [dataInDegrees, setDataInDegrees] = useState(false);
  const [blockingCol, setBlockingCol] = useState(false);
  const [croquisCaptura, setCroquisCaptura] = useState(null);
  const [dataForKML, setDataForKML] = useState([]);
  const [showKMLModal, setShowKMLModal] = useState(false);
  const [mapType, setMapType] = useState('hybrid');
  const [geoPuntos, setGeoPuntos] = useState([]);
  const [showSimbologia, setShowSimbologia] = useState(true);
  const [calculationsExecuted, setCalculationsExecuted] = useState(false);
  const {
    areaGMaps,
    renderMap,
    center,
    angle,
    pointsOnMap,
    pointsOnMapDegrees,
    lengths,
    zoom,
    map,
    maps,
    getElevations,
    centroidProcessed,
    hasPreloadedElevations,
  } = useCroquisHook(parcelaData);
  const geoPuntosForLayer = useMemo(() => (showSimbologia ? geoPuntos : []), [showSimbologia, geoPuntos]);
  const { legendData } = useGeoPuntosLayer(map, maps, geoPuntosForLayer);
  useArrowTextureOverlay(map, maps, parcelaData?.parcelaPolygon, angle, mapType);
  const { colindancias } = useGetColindancias({ uuid, setBlocking: setBlockingCol });
  const topHeight = 130;
  const fileUpload = createRef();
  const uploadKML = () => fileUpload.current.click();

  const handleKmlUpload = async e => {
    const file = e.target.files && e.target.files[0];
    // Reset so selecting the same file again still fires onChange
    e.target.value = '';
    if (!file) return;

    const errorTitle = `Algo falló al actualizar el croquis de la ${transformWord(etiquetas?.parcela, ['singular', 'lowercase'])}`;
    const validationError = validatePolygonFile(file);
    if (validationError) {
      Swal.fire({ icon: 'error', title: errorTitle, text: validationError });
      return;
    }

    const formData = new FormData();
    formData.append('uuid', uuid);
    formData.append('kml', file);
    Api.createMultiPart('kml_import', formData)
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: '',
          text: `Se actualizó con éxito la el croquis de la ${transformWord(etiquetas?.parcela, ['singular', 'lowercase'])}`,
          confirmButtonText: 'Continuar',
        }).then(result => {
          if (result.isConfirmed) {
            window.location.href = '/admin/parcelas/perfil/' + uuid;
          }
        });
      })
      .catch(error => {
        Swal.fire({
          icon: 'error',
          title: errorTitle,
          text: getApiError(error),
        });
      });
  };

  useEffect(() => {
    if (uuid != null) {
      Api.get('parcelas_croquis_captura', uuid)
        .then(response => {
          if (response.data && response.data.length > 0) {
            setCroquisCaptura(response.data);
          } else {
            setCroquisCaptura(env.noImage);
          }
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log('e', e);
        });
      Api.get('parcela_geojson', uuid)
        .then(response => {
          setDataForKML(response.data);
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log('e', e);
        });
      Api.get('parcela_geos_puntos', uuid)
        .then(response => setGeoPuntos(response.data || []))
        .catch(() => setGeoPuntos([]));
    }
  }, []);

  const dataPoints = dataInDegrees ? pointsOnMapDegrees : pointsOnMap;
  const hasCentroidOnly =
    !parcela.poligono && !!parcelaData?.centroid && !isNaN(parcelaData.centroid.lat) && !isNaN(parcelaData.centroid.lng);
  const hasLegend =
    !!parcela.poligono ||
    hasCentroidOnly ||
    legendData.points.length > 0 ||
    legendData.lines.length > 0 ||
    legendData.polygons.length > 0 ||
    legendData.texts.length > 0;

  const coordsCenter = coords => {
    const lat = coords.reduce((s, c) => s + c.lat, 0) / coords.length;
    const lng = coords.reduce((s, c) => s + c.lng, 0) / coords.length;
    return { lat, lng };
  };

  const panTo = pos => {
    if (map) map.panTo(pos);
  };

  return (
    <>
      <div
        style={{
          position: 'relative',
          marginTop: 46,
        }}>
        <div
          className="mb-2"
          style={{
            position: 'absolute',
            top: -52,
            left: 0,
            right: 0,
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            pointerEvents: 'none',
          }}>
          <div />
          <div style={{ justifySelf: 'center', pointerEvents: 'auto' }}>
            <FormSelectMaterial
              id="tipoMapa"
              error={false}
              label="Tipo Mapa"
              value={mapType}
              onChange={v => setMapType(v)}
              height={34}
              options={[
                { value: 'satellite', label: 'Satelital' },
                { value: 'terrain', label: 'Terreno' },
                { value: 'hybrid', label: 'Híbrido' },
              ]}
              showAll={false}
            />
          </div>
          <div style={{ justifySelf: 'end', pointerEvents: 'auto' }}>
            <Link to={{ pathname: `/admin/parcelas/perfil/print/${uuid}` }} className="btn btn-outline-secondary">
              <i className="fa fa-print" aria-hidden="true"></i> Versión para compartir
            </Link>
          </div>
        </div>

        {/* Top info cards */}
        <div className="row pb-4">
          <div className="col-lg-5 mb-2">
            <BlockUi tag="div" message="" blocking={blocking} renderChildren={true}>
              <CardContainer height={topHeight}>
                <div className="row">
                  <CardTitleParcela
                    cuenta_uuid={parcela.cuenta_uuid}
                    cuenta_name={parcela.cuenta}
                    parcela_name={parcela.nombre}
                    localidad={parcela.localidad}
                    municipio={parcela.municipio}
                    certifications={parcela.certifications}
                  />
                </div>
              </CardContainer>
            </BlockUi>
          </div>
          <div className="col-lg-4 mb-2">
            <BlockUi tag="div" message="" blocking={blocking} renderChildren={true}>
              <CardContainer height={topHeight} style={{ paddingLeft: 10, paddingRight: 10 }}>
                <div className="row">
                  <div className="col-2">
                    <img src={env.images.icon.area} style={{ height: '55px' }} />
                  </div>
                  <div className="col-5 pl-3">
                    <h4 className="font-lato-normal-400">
                      {(areaGMaps || 0).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      has
                    </h4>
                    <p className="font-lato-normal-500 pb-3" style={{ color: env.colors.neutral_700 }}>
                      calculadas (automático)
                    </p>
                  </div>
                  <div className="col-5">
                    <h4 className="font-lato-normal-400">
                      {(parcela.superficie || 0).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      has
                    </h4>
                    <p className="font-lato-normal-500 pb-3" style={{ color: 'rgb(91, 91, 91)' }}>
                      reportadas del productor/a
                    </p>
                  </div>
                </div>
              </CardContainer>
            </BlockUi>
          </div>
          <div className="col-lg-3 mb-2">
            <BlockUi tag="div" message="" blocking={blocking} renderChildren={true}>
              <CardContainer height={topHeight} style={{ paddingLeft: 16, paddingRight: 10 }}>
                <div className="row">
                  <div className="col-3">
                    {/*
                    // TODO: Change colmena icon, validate final icon design 
                    */}
                    <img
                      src={!usaMiel() ? env.images.icon.planta : env.images.icon.colmena}
                      style={{
                        height: !usaMiel() ? '30px' : '36px',
                        marginTop: '6px',
                      }}
                    />
                  </div>
                  <div className="col-4">
                    <h4 className="font-lato-normal-400">
                      {(parcela.plantas || 0).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                    </h4>
                    <p className="font-lato-normal-500 pb-3">{transformWord(etiquetas?.planta, ['plural', 'capitalize'])}</p>
                  </div>
                  <div className="col-5">
                    <h5 className="font-lato-normal-500 pb-3">{parcela.variedades}</h5>
                  </div>
                </div>
              </CardContainer>
            </BlockUi>
          </div>
          {/* Especies (solo aplica a cooperativas de miel) */}
          {coopConfig.config?.cooperativa?.producto?.id === env.productos.MIEL && parcela.especies && (
            <div className="col-lg-5 mb-2">
              <BlockUi tag="div" message="" blocking={blocking} renderChildren={true}>
                <CardContainer>
                  <div className="px-3 py-2">
                    <h6 className="font-lato-normal-400 m-b-1" style={{ color: '#789D16' }}>
                      ESPECIES
                    </h6>
                    <p className="m-b-0 font-lato-normal-500" style={{ fontSize: '1.1rem' }}>
                      {parcela.especies}
                    </p>
                  </div>
                </CardContainer>
              </BlockUi>
            </div>
          )}
        </div>

        {/* Map section */}
        <div className="row pb-2">
          <div className="col-lg-12">
            <BlockUi tag="div" message="" blocking={blocking || blockingCol} renderChildren={true}>
              <CardContainer>
                <Panel>
                  {/* Full-width map with colindancia overlays */}
                  <div style={{ position: 'relative', width: '100%', height: 480 }}>
                    <ColindanciaBox
                      title="Norte"
                      data={colindancias?.norte}
                      style={{ top: 8, left: '50%', transform: 'translateX(-50%)' }}
                    />
                    <ColindanciaBox
                      title="Sur"
                      data={colindancias?.sur}
                      style={{ bottom: 8, left: '50%', transform: 'translateX(-50%)' }}
                    />
                    <ColindanciaBox
                      title="Oeste"
                      data={colindancias?.oeste}
                      style={{ top: '50%', left: 8, transform: 'translateY(-50%)' }}
                    />
                    <ColindanciaBox
                      title="Este"
                      data={colindancias?.este}
                      style={{ top: '50%', right: 8, transform: 'translateY(-50%)' }}
                    />

                    <div className="map-rounded map-hide-report-error" style={{ height: '100%', width: '100%' }}>
                      <NorthIcon color={mapType === 'terrain' ? 'black' : 'white'} />

                      {parcela.poligono ? (
                        <>
                          <GoogleMapReact
                            bootstrapURLKeys={{
                              key: env.googleApiKey,
                              libraries: ['geometry', 'elevation'],
                            }}
                            options={() => ({ mapTypeId: mapType, keyboardShortcuts: false })}
                            center={center}
                            zoom={zoom}
                            yesIWantToUseGoogleMapApiInternals
                            onGoogleApiLoaded={({ map, maps }) => renderMap(map, maps)}>
                            {lengths.map((it, i) => (
                              <MapText
                                lat={it?.lat}
                                lng={it?.lng}
                                key={i}
                                text={it?.length + 'm'}
                                style={{ color: mapType === 'terrain' ? 'black' : 'white' }}
                              />
                            ))}
                            {pointsOnMap.map((it, i) => (
                              <MapText
                                lat={it?.lat}
                                lng={it?.lng}
                                key={i}
                                text={it?.title}
                                style={{ color: mapType === 'terrain' ? 'black' : 'white' }}
                              />
                            ))}
                          </GoogleMapReact>
                        </>
                      ) : null}

                      {parcela.poligono === null &&
                        parcelaData?.centroid &&
                        !isNaN(parcelaData?.centroid.lat) &&
                        !isNaN(parcelaData?.centroid.lng) && (
                          <GoogleMapReact
                            bootstrapURLKeys={{ key: env.googleApiKey, libraries: ['elevation'] }}
                            options={() => ({ mapTypeId: mapType, keyboardShortcuts: false })}
                            center={centroid}
                            zoom={zoom}
                            yesIWantToUseGoogleMapApiInternals
                            onGoogleApiLoaded={({ map, maps }) => renderMap(map, maps)}>
                            <MarkerInfoWindow
                              key={1}
                              selectedElement={1}
                              lat={parcelaData?.centroid?.lat}
                              lng={parcelaData?.centroid?.lng}
                              productor={false}
                              color={'#91B833'}
                            />
                          </GoogleMapReact>
                        )}

                      {parcela.centroide === null && parcela.poligono === null && (
                        <CardBasic
                          height={479}
                          label="Ubicación no disponible"
                          value={''}
                          style={{ backgroundColor: env.colors.neutral_1 }}
                        />
                      )}
                    </div>
                  </div>

                  {/* Buttons row */}
                  <div className="row mt-2 mb-2">
                    <div className="col-12 d-flex flex-wrap" style={{ gap: 30, padding: '6px 18px' }}>
                      <button
                        type="button"
                        onClick={() => abrirDibujoCroquis(croquisCaptura, window)}
                        className="btn btn-parcela-action"
                        disabled={!croquisCaptura}>
                        <i className="fa fa-eye mr-2" aria-hidden="true"></i> Ver dibujo
                      </button>
                      <button type="button" onClick={() => uploadKML(uuid)} className="btn btn-parcela-action">
                        <i className="fa fa-upload mr-2" aria-hidden="true"></i> Subir polígono
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowKMLModal(true)}
                        className="btn btn-parcela-action"
                        disabled={!dataForKML || dataForKML.length === 0 || !parcela.poligono}>
                        <i className="fa fa-download mr-2" aria-hidden="true"></i> Descargar polígono
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteCroquis(uuid, window)}
                        className="btn btn-parcela-action"
                        disabled={!parcela.poligono}>
                        <i className="fa fa-trash mr-2" aria-hidden="true"></i> Eliminar Croquis
                      </button>
                      <button
                        type="button"
                        onClick={() => parcela.poligono && history.push(`/admin/parcelas/perfil/editcroquis/${uuid}`)}
                        className="btn btn-parcela-action"
                        disabled={!parcela.poligono}>
                        <i className="fa fa-pencil-alt mr-2" aria-hidden="true"></i> Editar polígono
                      </button>
                    </div>
                    <div className="col-12">
                      <small className="text-muted" style={{ padding: '0 18px' }}>
                        Formatos admitidos para subir polígono: KML, GeoJSON o shapefile comprimido en .zip (máx. {MAX_POLYGON_FILE_SIZE_MB}{' '}
                        MB).
                      </small>
                    </div>
                  </div>

                  {/* SIMBOLOGÍA */}
                  <SimbologiaLegend
                    legendData={legendData}
                    panTo={panTo}
                    coordsCenter={coordsCenter}
                    hasData={hasLegend}
                    showToggleButton={geoPuntos.length > 0}
                    show={showSimbologia}
                    onToggleShow={() => setShowSimbologia(v => !v)}
                    extraArea={
                      parcela.poligono
                        ? { label: transformWord(etiquetas?.parcela, ['singular', 'capitalize']), onClick: () => panTo(center) }
                        : null
                    }
                    extraPoint={
                      hasCentroidOnly
                        ? {
                            label: transformWord(etiquetas?.parcela, ['singular', 'capitalize']),
                            color: '#91B833',
                            onClick: () => panTo(parcelaData.centroid),
                          }
                        : null
                    }
                    showEditButton={!!parcela.poligono || hasCentroidOnly}
                    onEditClick={() => history.push(`/admin/parcelas/perfil/editsimbologia/${uuid}`)}
                  />

                  <input type="file" ref={fileUpload} accept={POLYGON_FILE_ACCEPT} onChange={handleKmlUpload} style={{ display: 'none' }} />
                </Panel>
              </CardContainer>
            </BlockUi>
          </div>
        </div>

        {/* Especies card se renderiza junto a las tarjetas superiores (solo MIEL) */}

        {/* Coordinates & alerts */}
        <div className="row pb-4">
          <div className="col-lg-12">
            <BlockUi tag="div" message="" blocking={blocking || blockingCol} renderChildren={true}>
              <CardContainer height={width > 1100 ? 450 : 650}>
                <Panel>
                  <div className="row mb-3">
                    <div className="col-6">
                      <div className="row mb-3" style={{ textAlign: 'center' }}>
                        <div style={{ textAlign: 'center', width: '100%' }}>
                          <h4>Coordenadas</h4>
                        </div>
                      </div>
                      <div className="row mb-3">
                        <button
                          type="button"
                          onClick={() => setDataInDegrees(false)}
                          className={'btn btn-light '}
                          style={{
                            borderRadius: 10,
                            backgroundColor: !dataInDegrees ? '#F1F2E2' : '#F6F7F5',
                            borderWidth: 0,
                            marginRight: 10,
                          }}
                          disabled={false}>
                          {!dataInDegrees ? <i className="fa fa-check" style={{ color: !dataInDegrees ? 'black' : '#777771' }} /> : null}{' '}
                          <span style={{ color: !dataInDegrees ? 'black' : '#777771' }}>Decimales</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => setDataInDegrees(true)}
                          className={'btn btn-light'}
                          style={{
                            borderRadius: 10,
                            backgroundColor: dataInDegrees ? '#F1F2E2' : '#F6F7F5',
                            borderWidth: 0,
                          }}
                          disabled={false}>
                          {dataInDegrees ? <i className="fa fa-check" style={{ color: dataInDegrees ? 'black' : '#777771' }} /> : null}{' '}
                          <span style={{ color: dataInDegrees ? 'black' : '#777771' }}>Grados</span>
                        </button>

                        {!hasPreloadedElevations && (
                          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={() => {
                                getElevations();
                                setCalculationsExecuted(true);
                              }}
                              className="btn btn-outline-secondary btn-sirio"
                              disabled={calculationsExecuted}>
                              <i className="fa fa-calculator pr-2"></i>
                              Calcular altitudes
                            </button>
                          </div>
                        )}
                      </div>

                      <table className="table table-bordered" style={{ borderRadius: 10 }} type="text/css">
                        <tbody>
                          <tr className="font-weight-bold" style={{ backgroundColor: '#F2F1E8' }}>
                            <td>Punto</td>
                            <td>Latitud</td>
                            <td>Longitud</td>
                            <td>Altitud</td>
                          </tr>
                          <tr>
                            <td>CENTRAL</td>
                            <td>{dataInDegrees ? centroidProcessed.deg.lat : centroidProcessed.dec.lat}</td>
                            <td>{dataInDegrees ? centroidProcessed.deg.lng : centroidProcessed.dec.lng}</td>
                            <td>
                              {centroidProcessed.elevation}
                              {centroidProcessed.elevation ? 'm' : '-'}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <div style={{ overflowY: 'auto', height: '220px' }}>
                        <table className="table table-bordered" style={{ borderRadius: 10 }} type="text/css">
                          <tbody>
                            <tr className="font-weight-bold" style={{ backgroundColor: '#F2F1E8' }}>
                              <td>Punto</td>
                              <td>Latitud</td>
                              <td>Longitud</td>
                              <td>Altitud</td>
                            </tr>
                            {dataPoints.map((p, i) => (
                              <tr key={i}>
                                <td>{p.title}</td>
                                <td>{parsePoint(p.lat, 7, true)}</td>
                                <td>{parsePoint(p.lng, 7, true)}</td>
                                <td>
                                  {p.elevation}
                                  {p.elevation ? 'm' : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="row mb-3" style={{ textAlign: 'center' }}>
                        <div style={{ textAlign: 'center', width: '100%' }}>
                          <h4>Alertas automatizadas</h4>
                        </div>
                      </div>
                      <ParcelasAlertas uuid={uuid} hasPolygon={parcela.poligono !== null} />
                    </div>
                  </div>
                </Panel>
              </CardContainer>
            </BlockUi>
          </div>
        </div>
      </div>
      <ModalKMLDownload
        isOpen={showKMLModal}
        onClose={() => setShowKMLModal(false)}
        dataForKML={dataForKML}
        parcelaName={parcela.nombre}
        parcelaId={parcela.id}
      />
    </>
  );
};

export default ParcelaProfile;
